import { SimpleDocCommand } from '../../apps/DocsApp/commands/v5/types';
import { StandardToolResult } from '../../types/standardToolResults';
import { CommandExecutionContext } from '../../types/commands';
import { getApplicationForEntity } from '../../services/applicationRegistry';

export const entity_replace_all: SimpleDocCommand = {
  id: 'entity_replace_all',
  logicalName: 'entity_replace_all',
  version: 'v2',

  description: 'Replace all occurrences of text in an entity.',

  schema: {
    parameters: [
      { name: 'entityId', type: 'string', description: 'Entity ID', required: true },
      {
        name: 'findText',
        type: 'string',
        description: 'Text to find',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Find text cannot be empty';
          return null;
        }
      },
      { name: 'replaceWith', type: 'string', description: 'Replacement text (empty string = delete)', required: true },
      { name: 'caseSensitive', type: 'boolean', description: 'Case-sensitive search (default: false)', required: false },
      { name: 'wholeWord', type: 'boolean', description: 'Match whole words only (default: false)', required: false },
      { name: 'preview', type: 'boolean', description: 'Preview mode - show matches without replacing (default: false)', required: false },
      { name: 'maxReplacements', type: 'number', description: 'Maximum number of replacements (default: unlimited)', required: false }
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, findText, replaceWith, caseSensitive = false, wholeWord = false, preview = false, maxReplacements } = params;
    const startTime = Date.now();

    try {
      const app = await getApplicationForEntity(entityId);
      if (!app) return { status: 'error', message: 'Entity not found or not a v2 entity', format: 'plaintext' };

      const readResult = await app.handleEntityRead({ all: true });
      const nodes = readResult.nodes || [];

      if (nodes.length === 0) return { status: 'success', message: 'Entity has no content to search', data: { replacementsMade: 0, nodesSearched: 0 }, format: 'json' };

      const escapedText = findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = wholeWord ? `\\b${escapedText}\\b` : escapedText;
      const flags = caseSensitive ? 'g' : 'gi';
      const regex = new RegExp(pattern, flags);

      const matches: Array<{ nodeId: string; text: string; matchCount: number }> = [];
      let totalMatches = 0;

      for (const node of nodes) {
        const text = node.text || '';
        const nodeMatches = (text.match(regex) || []).length;
        if (nodeMatches > 0) { matches.push({ nodeId: node.id, text, matchCount: nodeMatches }); totalMatches += nodeMatches; }
      }

      console.log('[entity_replace_all] Found matches:', { totalMatches, nodesWithMatches: matches.length });

      if (totalMatches === 0) return { status: 'success', message: `No matches found for "${findText}"`, data: { replacementsMade: 0, nodesSearched: nodes.length, findText, replaceWith }, format: 'json' };

      if (preview) {
        const previewData = matches.slice(0, 10).map(m => ({ nodeId: m.nodeId, matchCount: m.matchCount, preview: m.text.substring(0, 100) + '...' }));
        return { status: 'success', message: `Found ${totalMatches} matches in ${matches.length} nodes (preview only)`, data: { preview: true, totalMatches, nodesWithMatches: matches.length, matches: previewData }, format: 'json' };
      }

      let replaced = 0;
      const operations: any[] = [];

      for (const match of matches) {
        if (maxReplacements && replaced >= maxReplacements) break;
        const newText = match.text.replace(regex, replaceWith);
        const nodeReplacements = match.matchCount;
        if (maxReplacements && replaced + nodeReplacements > maxReplacements) continue;
        operations.push({ op: 'update', target: { nodeId: match.nodeId }, attrs: { text: newText } });
        replaced += nodeReplacements;
      }

      if (operations.length > 0) await app.handleEntityWrite({ operation: 'batch', operations });

      console.log('[entity_replace_all] ✅ Replacements complete:', { replacementsMade: replaced, nodesAffected: operations.length });
      return { status: 'success', message: `Replaced ${replaced} instance${replaced === 1 ? '' : 's'} in ${operations.length} node${operations.length === 1 ? '' : 's'}`, data: { replacementsMade: replaced, nodesAffected: operations.length, nodesSearched: nodes.length, findText, replaceWith, options: { caseSensitive, wholeWord, preview } }, format: 'json', metadata: { executionTime: Date.now() - startTime } };

    } catch (error: any) {
      console.error('[entity_replace_all] Error:', error);
      return { status: 'error', message: 'Find/replace failed', error: { code: 'replace_error', category: 'execution' as const, details: error.message }, format: 'plaintext' };
    }
  }
};
